import React from 'react';
import { Row, Col, Alert } from 'reactstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect } from 'react-redux';
import classes from './Dashboard.module.scss';
import MotorPromoModal from './dashboard/MotorPromoModal';
import PartnerLink from '../../components/PartnerLink/PartnerLink';
import { getBrandingContext } from '../../utils/hostname';
import { getEduPlusBuyLink } from '../../utils/interriskLink';

const {
  isOrange,
  isInsurances,
  isNais: isNaisBrand,
  showFullDashboard,
  showShooterProtect,
  showRowerzysta
} = getBrandingContext();

const domainKey = isOrange
  ? 'orange'
  : isInsurances
    ? 'ubezpieczenia'
    : isNaisBrand
      ? 'nais'
      : 'tvp';

const dashboardThemeClass =
  isOrange || isInsurances
    ? classes.orangeTheme
    : isNaisBrand
      ? classes.naisTheme
      : classes.defaultTheme;

const healthPortalHref =
  isOrange || isInsurances
    ? 'https://www.orange.pl/ubezpieczenia.phtml'
    : 'https://tvp-tuwpzuw.uspartner.pl/';

const healthPortalHost =
  isOrange || isInsurances
    ? 'orange.pl/ubezpieczenia.phtml'
    : 'tvp-tuwpzuw.uspartner.pl';

const shooterPromoCode = isOrange
  ? 'ORANGE'
  : isNaisBrand
    ? 'NAIS'
    : isInsurances
      ? 'GUN'
      : 'TVP';

const links = {
  pethelp: {
    website: 'https://pethelp.pl/oferta/ubezpieczenie/'
  },
  zagraniczne: {
    tvp: 'https://www.axa-assistance.pl/ubezpieczenie-turystyczne/?externalpartner=13000170',
    orange:
      'https://www.axa-assistance.pl/ubezpieczenie-turystyczne/?externalpartner=13000170',
    ubezpieczenia:
      'https://www.axa-assistance.pl/ubezpieczenie-turystyczne/?externalpartner=13000170',
    nais: 'https://www.axa-assistance.pl/ubezpieczenie-turystyczne/?externalpartner=13100174'
  },
  assistance: {
    tvp: 'https://www.axa-assistance.pl/ubezpieczenie-pojazdu/?ExternalPartner=13000170',
    orange:
      'https://www.axa-assistance.pl/ubezpieczenie-pojazdu/?ExternalPartner=13000170',
    ubezpieczenia:
      'https://www.axa-assistance.pl/ubezpieczenie-pojazdu/?ExternalPartner=13000170',
    nais: 'https://www.axa-assistance.pl/ubezpieczenie-pojazdu/?ExternalPartner=13100174'
  },
  szkolne: {
    tvp: getEduPlusBuyLink(),
    orange: getEduPlusBuyLink(),
    ubezpieczenia: getEduPlusBuyLink(),
    nais: getEduPlusBuyLink()
  }
};

const partnerLogo = (file) => `${process.env.PUBLIC_URL}/tvp/partners/${file}`;

// External links always open in a new tab and carry the ↗ mark, as on uspartner.pl.
// A purchase on a partner's site first shows the "you are about to leave" dialog.
const ActionLink = ({
  children,
  className,
  href,
  to,
  external,
  partner,
  partnerDiscountNotice
}) => {
  if (!href && !to) {
    return null;
  }

  if (external) {
    const content = (
      <>
        {children}
        <span className={classes.ext} aria-hidden="true">
          ↗
        </span>
      </>
    );

    if (partner) {
      return (
        <PartnerLink
          className={className}
          discount={partnerDiscountNotice}
          href={href}
          partner={partner}>
          {content}
        </PartnerLink>
      );
    }

    return (
      <a
        className={className}
        href={href}
        rel="noopener noreferrer"
        target="_blank">
        {content}
      </a>
    );
  }

  return (
    <Link className={className} to={to}>
      {children}
    </Link>
  );
};

const CardLink = ({ icon, link, title }) => (
  <Link to={link} className={classes.noDecoration}>
    <div className={classes.dashboardButton}>
      <div>
        <FontAwesomeIcon fixedWidth icon={icon} />
      </div>
      <div>{title}</div>
    </div>
  </Link>
);

// Descriptions, labels, ribbons and "już od" prices follow the same products on
// uspartner.pl (usppublicsite src/content/products.ts); the prices match the
// TVP price tables (pethelp/data.js, edu/ProgramTable.js, majatkowe/TableFinances.js)
const productCards = [
  showFullDashboard && {
    key: 'pracownicy',
    img: 17,
    title: 'Opieka szpitalna',
    desc: 'Prywatna opieka szpitalna LUX MED dla Ciebie i Twojej rodziny.',
    logoSrc: partnerLogo('luxmed.webp'),
    logoAlt: 'LUX MED',
    detailsLabel: 'Poznaj ofertę',
    detailsTo: '/pracownicy/opis/',
    actionLabel: 'Zgłoś chęć ubezpieczenia',
    actionTo: '/pracownicy/kup/',
    footNote: 'Wymagana oddzielna rejestracja i logowanie'
  },
  {
    key: 'zagraniczne',
    featured: true,
    img: 20,
    title: 'Podróże zagraniczne',
    desc: 'Koszty leczenia do 40 mln zł, bagaż, OC i assistance 24/7 — na urlop i wyjazdy służbowe.',
    note: 'Świat · Europa',
    priceFrom: '5,75 zł/dzień',
    ribbon: { type: 'top', label: 'Najczęściej wybierany produkt' },
    logoSrc: partnerLogo('axa.svg'),
    logoAlt: 'AXA',
    partner: 'AXA',
    detailsLabel: 'Poznaj ofertę',
    detailsTo: '/zagraniczne/opis/',
    actionLabel: 'Kup online',
    actionHref: links.zagraniczne[domainKey],
    actionExternal: true
  },
  !isOrange &&
    !isNaisBrand && {
      key: 'pethelp',
      img: 81,
      title: 'Ubezpieczenie zwierząt',
      desc: 'Badania, szczepienia i wizyty u weterynarza dla psa i kota — w ponad 869 placówkach w Polsce.',
      note: 'Pies · Kot',
      priceFrom: '42 zł/mies.',
      ribbon: { type: 'new', label: 'Nowość' },
      logoSrc: partnerLogo('pethelp.png'),
      logoAlt: 'Pethelp',
      detailsLabel: 'Poznaj ofertę',
      detailsTo: '/pethelp/opis/',
      actionLabel: 'Oblicz składkę',
      actionTo: '/pethelp/pakiet-dla-psa/'
    },
  showRowerzysta && {
    key: 'rowerzysta',
    img: 82,
    title: 'Rowerzysta',
    desc: 'Casco roweru, bagaż, OC, NNW i assistance — także dla dziecka w foteliku.',
    note: 'Rower · Trasa',
    ribbon: { type: 'info', label: 'Twój styl, Twoja trasa' },
    logoSrc: partnerLogo('pzu.svg'),
    logoAlt: 'PZU',
    partner: 'PZU',
    detailsLabel: 'Poznaj ofertę',
    detailsTo: '/rowerzysta/opis/',
    actionLabel: 'Kup online',
    actionHref: 'https://ubestrefa.pl/oferta/ZPYIGF',
    actionExternal: true
  },
  {
    key: 'edu',
    img: 10,
    title: 'NNW szkolne',
    desc: 'Ochrona dziecka 365 dni w roku na całym świecie — także sport, Hejt Stop i Pakiet Kleszcz.',
    note: 'Dzieci · Młodzież',
    priceFrom: '73 zł/rok',
    ribbon: { type: 'info', label: 'Program na rok 2026/2027' },
    logoSrc: partnerLogo('interrisk.svg'),
    logoAlt: 'InterRisk',
    partner: 'InterRisk',
    detailsLabel: 'Poznaj ofertę',
    detailsTo: '/edu/opis/',
    actionLabel: 'Kup online',
    actionHref: links.szkolne[domainKey],
    actionExternal: true
  },
  {
    key: 'assistance',
    featured: true,
    img: 21,
    title: 'Moto Assistance',
    desc: 'Holowanie, auto zastępcze i pomoc w trasie po awarii, wypadku, kradzieży czy przebitej oponie.',
    note: '24/7',
    ribbon: { type: 'info', label: 'Polska i Europa' },
    logoSrc: partnerLogo('axa.svg'),
    logoAlt: 'AXA',
    partner: 'AXA',
    detailsLabel: 'Poznaj ofertę',
    detailsTo: '/assistance/opis/',
    actionLabel: 'Kup online',
    actionHref: links.assistance[domainKey],
    actionExternal: true
  },
  {
    key: 'komunikacyjne',
    featured: true,
    img: 83,
    title: (
      <>
        Ubezpieczenie Twojego Auta
        <br />— Pakiety Dealerskie
      </>
    ),
    desc: 'OC, AC, assistance i NNW — pakiety PZU, Allianz, Generali, Ergo Hestia, InterRisk i Uniqa',
    note: 'Osobowe · Dostawcze do 3,5 t · Kampery',
    ribbon: { type: 'info', label: '6 towarzystw do wyboru' },
    detailsLabel: 'Poznaj ofertę',
    detailsTo: '/komunikacyjne/opis/',
    actionKind: 'advisor',
    actionLabel: 'Zapytaj doradcę',
    actionTo: '/komunikacyjne/opis/#formularz'
  },
  showShooterProtect && {
    key: 'shooter',
    img: 84,
    title: 'Posiadacze broni',
    desc: 'OC, NNW i ochrona prawna — specjalistyczna oferta dla posiadaczy pozwolenia na broń.',
    note: 'OC · NNW · Ochrona prawna',
    ribbon: {
      type: 'promo',
      label: `Kod promocyjny: ${shooterPromoCode} · 15% zniżki`
    },
    logoSrc: partnerLogo('colonnade.webp'),
    logoAlt: 'Colonnade',
    partner: 'ShooterProtect',
    partnerDiscount: `kod ${shooterPromoCode}: −15%`,
    partnerDiscountNotice: `Przy zakupie wpisz kod ${shooterPromoCode} — dostaniesz 15% zniżki.`,
    detailsLabel: 'Poznaj ofertę',
    detailsHref: 'https://shooterprotect.pl/',
    detailsExternal: true,
    actionLabel: 'Kup online',
    actionHref: 'https://shooterprotect.pl/oferta/',
    actionExternal: true
  },
  showFullDashboard && {
    key: 'zdrowotne',
    img: 13,
    title: 'Zdrowotne',
    desc: 'Ubezpieczenie zdrowotne TUW PZUW, dostępne w dedykowanym serwisie.',
    logoSrc: isOrange ? null : `${process.env.PUBLIC_URL}/tvp/tuw_logo.png`,
    logoAlt: 'TUW PZUW',
    actionLabel: 'Przejdź do serwisu',
    actionHref: healthPortalHref,
    actionExternal: true,
    footNote: `↗ Dostępne na ${healthPortalHost}`
  },
  {
    key: 'likwidacja',
    img: 22,
    title: 'Zgłoszenie szkody komunikacyjnej',
    desc: 'Szybkie zgłoszenie szkody z OC i AC — darmowe auto zastępcze i bezpłatne wsparcie.',
    detailsLabel: 'Proces',
    detailsTo: '/likwidacja/opis/',
    actionKind: 'claim',
    actionLabel: 'Zgłoś szkodę',
    actionHref: 'https://zglosicszkode.pl/',
    actionExternal: true
  },
  {
    key: 'majatkowe',
    img: 11,
    title: 'Majątkowe',
    desc: 'Dom, mieszkanie i Twój dobytek — z OC w życiu prywatnym i assistance dla domu.',
    note: 'Dom · Mieszkanie',
    priceFrom: '299 zł/rok',
    logoSrc: partnerLogo('interrisk.svg'),
    logoAlt: 'InterRisk',
    detailsLabel: 'Poznaj ofertę',
    detailsTo: '/majatkowe/opis/',
    actionKind: 'advisor',
    actionLabel: 'Zapytaj doradcę',
    actionTo: '/majatkowe/kup/'
  }
].filter(Boolean);

const highlightedCardKeys =
  isOrange || isNaisBrand
    ? [
        'zagraniczne',
        'assistance',
        'komunikacyjne',
        'majatkowe',
        'shooter',
        'likwidacja'
      ]
    : [
        'zagraniczne',
        'assistance',
        'komunikacyjne',
        'majatkowe',
        'shooter',
        'pethelp'
      ];

const highlightedCards = highlightedCardKeys
  .map((key) => productCards.find((product) => product.key === key))
  .filter(Boolean);

const standardCardKeys =
  isOrange || isNaisBrand
    ? ['edu', 'rowerzysta']
    : ['edu', 'pracownicy', 'rowerzysta', 'likwidacja'];

const standardCards = standardCardKeys
  .map((key) => productCards.find((product) => product.key === key))
  .filter(Boolean);

const careAndPropertyCards = productCards.filter((product) =>
  ['zdrowotne'].includes(product.key)
);

const RIBBON_CLASS = {
  top: classes.ribbonTop,
  new: classes.ribbonNew,
  info: classes.ribbonInfo,
  promo: classes.ribbonPromo
};

// Orange = buy, blue = talk to an advisor, red = report a claim (only that)
const ACTION_CLASS = {
  buy: classes.btnCta,
  advisor: classes.btnPrimary,
  claim: classes.btnClaim
};

const ProductCard = ({
  actionExternal,
  actionHref,
  actionKind = 'buy',
  actionLabel,
  actionTo,
  desc,
  detailsExternal,
  detailsHref,
  detailsLabel,
  detailsTo,
  featured,
  footNote,
  img,
  logoAlt,
  logoSrc,
  note,
  partner,
  partnerDiscount,
  partnerDiscountNotice,
  priceFrom,
  ribbon,
  title
}) => (
  <article
    className={`${classes.pcard} ${featured ? classes.pcardFeatured : ''}`}>
    <div className={classes.pcardMedia}>
      <img
        className={classes.pcardPhoto}
        src={`${process.env.PUBLIC_URL}/tvp/dashboard/${img}.jpg`}
        alt=""
      />
      <div className={classes.pcardGrad} aria-hidden="true" />
      <div className={classes.pcardTop}>
        <h3 className={classes.pcardTitle}>{title}</h3>
        {logoSrc && (
          <span className={classes.pcardPartner}>
            <img
              className={classes.pcardPartnerLogo}
              src={logoSrc}
              alt={logoAlt}
            />
          </span>
        )}
      </div>
      {ribbon && (
        <span
          className={`${classes.pcardRibbon} ${RIBBON_CLASS[ribbon.type]}`}>
          {ribbon.label}
        </span>
      )}
    </div>
    <div className={classes.pcardBody}>
      <p className={classes.pcardTag}>{desc}</p>
      {(note || priceFrom) && (
        <div className={classes.pcardMeta}>
          {note && <span className={classes.pcardNote}>{note}</span>}
          {priceFrom && (
            <span className={classes.pcardPrice}>
              już od <strong>{priceFrom}</strong>
            </span>
          )}
        </div>
      )}
      <div className={classes.pcardActions}>
        <ActionLink
          className={`${classes.pcardBtn} ${classes.btnGhost}`}
          external={detailsExternal}
          href={detailsHref}
          to={detailsTo}>
          {detailsLabel}
          {!detailsExternal && (
            <span className={classes.arrow} aria-hidden="true">
              →
            </span>
          )}
        </ActionLink>
        <ActionLink
          className={`${classes.pcardBtn} ${ACTION_CLASS[actionKind]}`}
          external={actionExternal}
          href={actionHref}
          partner={partner}
          partnerDiscountNotice={partnerDiscountNotice}
          to={actionTo}>
          {actionLabel}
        </ActionLink>
      </div>
      {actionExternal && partner && (
        <p className={classes.pcardFoot}>
          <span aria-hidden="true">↗</span> Zakup na bezpiecznej stronie{' '}
          {partner}
          {partnerDiscount && (
            <>
              {' · '}
              <strong>{partnerDiscount}</strong>
            </>
          )}
        </p>
      )}
      {footNote && <p className={classes.pcardFoot}>{footNote}</p>}
    </div>
  </article>
);

const Dashboard = (props) => (
  <div className={`${classes.dashboardShell} ${dashboardThemeClass}`}>
    {props.isAuthenticated && props.luxmed && (
      <Row className={classes.toolsRow}>
        <Col xs={6} md={3}>
          <CardLink
            link="/c3d881ba-9109-4c24-8e92-45d2bf8b5609"
            title="Moje wnioski"
            icon="file-signature"
          />
        </Col>
      </Row>
    )}

    <section className={classes.catalogSection}>
      <div className={classes.sectionHeader}>
        <div>
          <span className={classes.sectionEyebrow}>Top wybory</span>
          <h2 className={classes.sectionTitle}>Najczęściej wybierane</h2>
          <p className={classes.sectionLead}>
            Wybierz ochronę, która najlepiej odpowiada Twoim planom i stylowi
            życia.
          </p>
        </div>
      </div>

      <Row className={classes.cardRow}>
        {highlightedCards.map((product) => (
          <Col key={product.key} className={classes.cardCol} lg={4} md={6} sm={12}>
            <ProductCard {...product} />
          </Col>
        ))}
      </Row>
      <div className={classes.sectionSpacer} />
      <div className={classes.sectionHeader}>
        <div>
          <span className={classes.sectionEyebrow}>Więcej możliwości</span>
          <h2
            className={`${classes.sectionTitle} ${classes.sectionTitleNowrap}`}>
            Dobierz ochronę i wsparcie do swoich potrzeb
          </h2>
          <p className={classes.sectionLead}>
            Sprawdź dodatkowe rozwiązania, które rozszerzają Twój zakres
            ochrony.
          </p>
        </div>
      </div>

      <Row className={classes.cardRow}>
        {standardCards.map((product) => (
          <Col key={product.key} className={classes.cardCol} lg={4} md={6} sm={12}>
            <ProductCard {...product} />
          </Col>
        ))}

        {(isOrange || isInsurances) && (
          <Col sm={12} className={classes.cardCol}>
            <Alert className={classes.inlineAlert} color="secondary">
              W sprawie innych prywatnych ubezpieczeń, np. komunikacyjnych,
              skontaktuj się z nami bezpośrednio.
              <br />
              <b>Infolinia:</b> 22 417 34 40
              <br />
              <b>E-mail:</b>{' '}
              {isOrange ? 'orange@uspartner.pl' : 'biuro@uspartner.pl'}
            </Alert>
          </Col>
        )}
      </Row>

      {careAndPropertyCards.length > 0 && (
        <>
          <div className={classes.sectionSpacer} />
          <div className={classes.sectionHeader}>
            <div>
              <span className={classes.sectionEyebrow}>Spokój na co dzień</span>
              <h2 className={classes.sectionTitle}>Zdrowie pod opieką</h2>
              <p className={classes.sectionLead}>
                Rozwiązania zdrowotne dla Ciebie i Twoich bliskich w jednym
                miejscu.
              </p>
            </div>
          </div>

          <Row className={classes.cardRow}>
            {careAndPropertyCards.map((product) => (
              <Col key={product.key} className={classes.cardCol} lg={4} md={6} sm={12}>
                <ProductCard {...product} />
              </Col>
            ))}
          </Row>
        </>
      )}
    </section>
    <MotorPromoModal />
  </div>
);

const mapStateToProps = (state) => {
  const programs = state.auth.programs || [];

  return {
    isAuthenticated: state.auth.token !== null,
    username: state.auth.user.name,
    luxmed:
      state.auth.user.programId === 'f1912316-3516-44a0-b58b-4c24b584f486',
    hasPrograms: programs.length > 1,
    menu: state.menu.data && state.menu.data.menu ? state.menu.data.menu : []
  };
};

export default connect(mapStateToProps)(Dashboard);
